import React, { useEffect, useState } from "react";
import { useLang, useCart, useSettings, fmtDzd } from "../lib/providers";
import { X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

export function PromoBar() {
  const { lang } = useLang();
  const { settings } = useSettings();
  if (!settings.promo_bar_enabled) return null;
  const items = (lang === "ar" ? settings.promo_bar_items_ar : settings.promo_bar_items_fr) || [];
  if (items.length === 0) return null;
  const list = [...items, ...items, ...items];
  return (
    <div className="bg-themed-deep border-b border-neon/20 py-2 overflow-hidden" data-testid="promo-bar">
      <div className="marquee gap-10 px-4">
        {list.map((s, i) => (
          <div key={i} className="flex items-center gap-10 text-[11px] font-heading font-black uppercase tracking-[0.2em] text-neon whitespace-nowrap">
            <span>{s}</span>
            <span className="w-1 h-1 rounded-full bg-neon/50" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function FreeShippingBanner({ subtotalDzd } = {}) {
  const { lang } = useLang();
  const { totalEur } = useCart();
  const { settings } = useSettings();
  const totalDzd = subtotalDzd != null ? subtotalDzd : totalEur * settings.exchange_rate;

  // Prochain avantage le plus proche (cadeau par palier OU seuil Stop Desk/Domicile) —
  // un seul message à la fois, jamais plusieurs affichés simultanément.
  const candidates = [];
  for (const t of (settings.gift_tiers || [])) {
    const thr = Number(t?.threshold_dzd || 0);
    if (t?.enabled !== false && t?.product_id && thr > 0 && totalDzd < thr) {
      candidates.push({ type: "gift", threshold: thr, remaining: thr - totalDzd });
    }
  }
  const thrStopdesk = Number(settings.free_shipping_threshold_stopdesk_dzd || 0);
  if (thrStopdesk > 0 && totalDzd < thrStopdesk) {
    candidates.push({ type: "stopdesk", threshold: thrStopdesk, remaining: thrStopdesk - totalDzd });
  }
  const thrHome = Number(settings.free_shipping_threshold_home_dzd || 0);
  if (thrHome > 0 && totalDzd < thrHome) {
    candidates.push({ type: "home", threshold: thrHome, remaining: thrHome - totalDzd });
  }
  candidates.sort((a, b) => a.remaining - b.remaining);
  const next = candidates[0] || null;

  const hasAnyConfigured = (settings.gift_tiers || []).some((t) => t?.enabled !== false && t?.product_id && Number(t?.threshold_dzd || 0) > 0)
    || thrStopdesk > 0 || thrHome > 0;

  if (totalEur === 0 || !hasAnyConfigured) return null;

  const messages = {
    gift: { fr: (r) => `🎁 Plus que ${fmtDzd(r)} pour débloquer votre prochain cadeau !`, ar: (r) => `🎁 ${fmtDzd(r)} أضف لفتح هديتك القادمة!` },
    stopdesk: { fr: (r) => `🚚 Plus que ${fmtDzd(r)} pour la livraison Stop Desk offerte !`, ar: (r) => `🚚 ${fmtDzd(r)} أضف لتوصيل Stop Desk مجاني!` },
    home: { fr: (r) => `🏠 Plus que ${fmtDzd(r)} pour la livraison à domicile offerte !`, ar: (r) => `🏠 ${fmtDzd(r)} أضف للتوصيل المنزلي المجاني!` },
  };
  const label = next
    ? (lang === "ar" ? messages[next.type].ar(next.remaining) : messages[next.type].fr(next.remaining))
    : (lang === "ar" ? "🎉 كل المزايا مفتوحة!" : "🎉 Tous les avantages sont débloqués !");
  const progress = next ? Math.min(100, (totalDzd / next.threshold) * 100) : 100;

  return (
    <div className="border border-neon/30 bg-neon/5 p-3 my-4" data-testid="free-shipping-banner">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-neon">{label}</span>
      </div>
      <div className="h-1.5 bg-white/10 overflow-hidden">
        <div className="h-full bg-neon transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

export function FomoPopup() {
  const { lang } = useLang();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const FOMO_KEY = "fomo_seen_v2";

  useEffect(() => {
    if (!settings.fomo_enabled) return;
    if (localStorage.getItem(FOMO_KEY)) return;
    // Anti-overlap: don't show if another popup was already shown this session
    if (sessionStorage.getItem("site_popup_shown")) return;
    const onLeave = (e) => { if (e.clientY < 20) trigger(); };
    const timer = setTimeout(trigger, (settings.fomo_delay_seconds || 35) * 1000);
    document.addEventListener("mouseout", onLeave);
    function trigger() {
      if (localStorage.getItem(FOMO_KEY)) return;
      if (sessionStorage.getItem("site_popup_shown")) return;
      setOpen(true);
      localStorage.setItem(FOMO_KEY, "1");
      sessionStorage.setItem("site_popup_shown", "1");
      clearTimeout(timer);
      document.removeEventListener("mouseout", onLeave);
    }
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onLeave);
    };
  }, [settings.fomo_enabled, settings.fomo_delay_seconds]);

  if (!settings.fomo_enabled || !open) return null;

  const kicker = lang === "ar" ? settings.fomo_kicker_ar : settings.fomo_kicker_fr;
  const title = lang === "ar" ? settings.fomo_title_ar : settings.fomo_title_fr;
  const desc = lang === "ar" ? settings.fomo_desc_ar : settings.fomo_desc_fr;
  const code = settings.fomo_code || "TENNIS10";
  const waPhone = (settings.whatsapp_dz || "").replace(/\D/g, "");

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="fomo-popup">
      <div className="relative w-full max-w-md bg-[var(--c-bg)] border border-neon/40 p-8 text-center" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setOpen(false)} className="absolute top-3 end-3 text-white/40 hover:text-white" aria-label="Close" data-testid="fomo-close">
          <X className="w-5 h-5" />
        </button>
        <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase text-neon mb-4 border border-neon/30 px-2 py-1">
          <Sparkles className="w-3 h-3" />{kicker}
        </div>
        <h3 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-[0.9]">{title}</h3>
        <p className="text-white/70 text-sm mt-4">{desc}</p>
        <div className="my-6 p-4 border-2 border-dashed border-neon bg-neon/5">
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Code</div>
          <div className="font-heading font-black text-4xl text-neon">{code}</div>
        </div>
        <Button asChild className="w-full bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest py-5 h-auto" data-testid="fomo-cta">
          <a href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`${lang === "ar" ? settings.wa_message_template_ar : settings.wa_message_template_fr} code ${code}`)}`} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
            {lang === "ar" ? "اطلب عبر واتساب" : "Commander sur WhatsApp"}
          </a>
        </Button>
        <button onClick={() => setOpen(false)} className="text-xs text-white/40 hover:text-white mt-3 underline underline-offset-4">
          {lang === "ar" ? "لاحقا" : "Plus tard"}
        </button>
      </div>
    </div>
  );
}
